import 'react-perfect-scrollbar/dist/css/styles.css';
import React, { useEffect, useContext, useRef, useState } from 'react';
import { useRoutes, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core';
import GlobalStyles from './components/GlobalStyles';
import { defaultTheme, customTheme } from '../src/theme';
import routes from '../src/routes';
import { useInterval } from './automate/automate-interval-runner.component';
import EdgeOrREST from './EdgeOrREST';
import { AuthContext } from './auth-provider.component';
import { setAxiosHeaders } from './API/services/axios.client';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import selectActions from './store/actions/selectActions';
import userActions from './store/actions/user';
import { initialState } from './store/initial-state';

const App = () => {
    const { authed, logout, login, token, userId, companyId, is2FAAuthenticated, isAuthResolving } = useContext(AuthContext);
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const state = useSelector(state => state)
    const location = useLocation();
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isRestoringSession, setIsRestoringSession] = useState(() =>
        window.localStorage.getItem("remember") == "true"
        && !!window.localStorage.getItem("token")
        && window.location.href.indexOf("forgot") == -1
    );
    const bootstrappedSessionRef = useRef(null);
    const shouldHoldRouting = isRestoringSession || isAuthResolving;
    const routing = useRoutes(routes(shouldHoldRouting ? false : authed, shouldHoldRouting ? false : is2FAAuthenticated));
    const excludedKeepAlivePaths = [
        "/resetPassword",
        "/login",
        "/launcher",
        "/monitor",
        "/userAdmin",
        "/forgot",
        "/2fa-setup",
    ];
    const isKeepAlivePathExcluded = excludedKeepAlivePaths.some(path => location.pathname.includes(path));
    const shouldRunKeepAlive = authed
        && is2FAAuthenticated
        && !!token
        && !isKeepAlivePathExcluded;

    const allowedPaths = [
        "/optimize",
        "/automate",
        "/photometry",
        "/plan",
        "/design",
    ];

    useInterval(() => {
        if (shouldRunKeepAlive) {
            EdgeOrREST(null, 'KEEP_ALIVE', onKeepAlive, null);
        }
    }, 25000);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => {
            setIsOnline(false);
            alert('You are disconnected from the internet. Please check your connection.');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        window.localStorage.removeItem("redirectedFromLauncher");
        if (window.localStorage.getItem("remember") == "true" && window.localStorage.getItem("token") && window.location.href.indexOf("forgot") == -1) {
            console.log("checking token")
            setAxiosHeaders(window.localStorage.getItem("token"));
            EdgeOrREST(null, 'CHECK_TOKEN', onCheckToken, null);
        } else {
            setIsRestoringSession(false);
        }
        if (!window.localStorage.getItem("token")) {
            window.localStorage.setItem("token", "adkmoADBYn!da@");
        }
    }, []);

    useEffect(() => {
        if (shouldRunKeepAlive) {
            EdgeOrREST(null, 'KEEP_ALIVE', onKeepAlive, null);
        }
    }, [shouldRunKeepAlive]);

    useEffect(() => {
        if (authed && allowedPaths.some(path => location.pathname.includes(path))) {
            window.localStorage.setItem("lastPath", location.pathname);
            console.log("saved last path", location.pathname);
        }
    }, [location.pathname, authed]);

    useEffect(() => {
        if (!authed || !is2FAAuthenticated || !token || !userId || !companyId) {
            bootstrappedSessionRef.current = null;
            return;
        }

        const sessionKey = `${token}:${userId}:${companyId}`;
        if (bootstrappedSessionRef.current === sessionKey) {
            return;
        }

        bootstrappedSessionRef.current = sessionKey;
        EdgeOrREST(null, 'GET_CFG', getThemeCfg, { 'companyId': companyId });
        EdgeOrREST(null, 'GET_ROLE_ID', getRoleId, { headers: { 'userId': userId } });
        dispatch(userActions.UpdateUserCfg({ roles: undefined, rolesLoading: true }));
        EdgeOrREST(null, 'GET_ROLES', getRoles, { headers: { 'id': userId } });
        EdgeOrREST(null, 'GET_PRODUCTS', getProducts, { headers: { 'id': companyId } });
    }, [authed, is2FAAuthenticated, token, userId, companyId]);

    const newShade = (hexColor, magnitude) => {
        hexColor = hexColor.replace(`#`, ``);
        if (hexColor.length === 6) {
            const decimalColor = parseInt(hexColor, 16);
            let r = (decimalColor >> 16) + magnitude;
            r > 255 && (r = 255);
            r < 0 && (r = 0);
            let g = (decimalColor & 0x0000ff) + magnitude;
            g > 255 && (g = 255);
            g < 0 && (g = 0);
            let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
            b > 255 && (b = 255);
            b < 0 && (b = 0);
            return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
        } else {
            return hexColor;
        }
    };

    const getThemeCfg = (response) => {
        if (response.data != null) {
            let string = response.data
            let colour = ("#" + string.substring(1, 7));
            let logo = ("data:image/png;base64," + string.substring(7));
            if (logo == "data:image/png;base64,") {
                logo = initialState.Select.logo
            }
            const t = newShade(colour, -30);
            dispatch(selectActions.UpdateLogo({ logo: logo }))
            dispatch(selectActions.UpdatePrimary({ primary: colour }))
            dispatch(selectActions.UpdateTertiary({ tertiary: t }))
            dispatch(selectActions.UpdateText({ fileText: "Change Logo" }))
            setTimeout(() => dispatch(selectActions.UpdateTheme({ theme: true })), 300)
        }
    }

    const getRoleId = (response) => {
        if (response.data != 0 && response.data != null) {
            let roleNum = response.data
            if (roleNum == 1) {
                dispatch(selectActions.UpdateUserRole({ userRole: "admin" }))
            }
        }
        else {
            dispatch(selectActions.UpdateUserRole({ userRole: "not admin" }))
            console.log("Not a Select Admin")
        }
    }

    const getProducts = (response) => {
        if (response.data) {
            dispatch(userActions.UpdateUserCfg({
                products: response.data
            }))
        }
    }

    const getRoles = (response) => {
        dispatch(userActions.UpdateUserCfg({
            roles: Array.isArray(response?.data) ? response.data : [],
            rolesLoading: false
        }))
    }

    const onCheckToken = async (response) => {
        console.log("checked token", response);
        if (response.data && response.status == '200') {
            console.log("after check token", response.data.companyId);
            dispatch(userActions.UpdateUserCfg({userId: response.data.id}))
            await login(window.localStorage.getItem("token"), response.data.id, response.data.companyId, false, {
                requiresTwoFactor: response.data.requiresTwoFactor,
                isTwoFactorVerified: response.data.isTwoFactorVerified,
            });
        } else {
            logout();
        }
        setIsRestoringSession(false);
    }

    const onKeepAlive = (response) => {
        console.log("keep alive", response);
        if (response && response.status != '200') {
            window.localStorage.removeItem("redirectedFromLauncher");
            logout();
            navigate('/login');
        }
    }

    return (
        <ThemeProvider theme={state.Select.theme ? customTheme : defaultTheme}>
            <GlobalStyles />
            {shouldHoldRouting ? null : routing}
        </ThemeProvider>
    );
};

export default App;
